import { msg } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { format } from 'date-fns';
import { Form, Formik } from 'formik';
import { useOrganizer } from 'modules/organizer/ui/hooks/use-organizer';
import { upsertTournamentUser } from 'modules/players/store/actions';
import { teamPlayerValidator } from 'modules/players/ui/components/player-form';
import { mapPlayerToPlayerApplication } from 'modules/teams/domain/data-mappers/player-player-application.mapper';
import { PlayerApplication } from 'modules/teams/domain/interfaces/player-application';
import { updatePlayerApplication } from 'modules/teams/store/championship-teams';
import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { useImageUpscaleFieldOption } from 'shared/hooks/use-image-upscale-access';
import { AppDispatch } from 'store/store';
import { UDFormDropzone } from 'ud-form/components/image-dropzone';
import { birthDateFormatter } from 'ud-form/components/input/domain/formatters/birth-date-formatter';
import UDFormInput from 'ud-form/components/input/index';
import { UDModalDirtyGuard } from 'ud-form/components/modal-dirty-guard';
import UDButton from 'ud-ui/components/button';
import Modal from 'ud-ui/components/modal';
import UDText from 'ud-ui/components/text';
import { useToastApiError } from 'shared/hooks/use-toast-api-error';

const getBirthDate = (birthDate: string) => {
  return format(new Date(birthDate), 'dd.MM.yyyy');
};

const useModalEditApplication = () => {
  const { t } = useLingui();
  const toastApiError = useToastApiError();
  const photoUpscale = useImageUpscaleFieldOption('photo');
  const dispatch = useDispatch<AppDispatch>();
  const [data, setData] = useState<PlayerApplication | null>(null);
  const [teamId, setTeamId] = useState<number | null>(null);
  const [formValues, setFormValues] = useState<any>(null);
  const { info, isLoading } = useOrganizer();
  const [isOpen, setIsOpen] = useState(false);
  const open = useCallback(() => {
    setIsOpen(true);
  }, []);
  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const onOpen = useCallback(
    (player: PlayerApplication, teamId: number) => {
      setData(player);
      setTeamId(teamId);
      setFormValues({
        surname: player.player?.lastName,
        name: player.player?.firstName,
        middleName: player.player?.middleName,
        birthDate: player.player?.birthDate ? getBirthDate(player.player.birthDate) : '',
        photo: player.player?.logoUrl ? [player.player.logoUrl] : null,
      });
      open();
    },
    [open],
  );

  // `async`: брошенная на ранней ветке ошибка становится отклонённым промисом — Formik снимет `isSubmitting`,
  // а не оставит «Сохранить» запертой. Кнопку на время запроса запирает сам промис (IMGM-1561).
  const handleSubmit = useCallback(
    async (values: any) => {
      if (!info) {
        toast.error(t`Произошла ошибка, попробуйте перезагрузить страницу`);
        throw new Error('Organizer info not found|info: ' + JSON.stringify(info, null, 2));
      }

      if (!teamId) {
        toast.error(t`Произошла ошибка, попробуйте перезагрузить страницу`);
        throw new Error('Team id not found|teamId: ' + JSON.stringify(teamId, null, 2));
      }

      if (!data) {
        toast.error(t`Произошла ошибка, попробуйте перезагрузить страницу`);
        throw new Error('Data not found|data: ' + JSON.stringify(data, null, 2));
      }

      setFormValues(values);
      return dispatch(
        upsertTournamentUser({
          userId: data?.player?.id,
          tournamentId: info.id,
          name: values.name,
          surname: values.surname,
          middleName: values.middleName,
          birthDate: values.birthDate,
          height: +values.height,
          weight: +values.weight,
          photo: values.photo || null,
        }),
      )
        .unwrap()
        .then((responsePlayer) => {
          dispatch(
            updatePlayerApplication({
              teamId,
              id: data.id,
              application: {
                ...data,
                player: mapPlayerToPlayerApplication(responsePlayer).player,
              },
            }),
          );
          close();
          toast.success(t`Игрок успешно сохранён`);
        })
        .catch((error) => {
          toastApiError(error, msg`Не удалось сохранить игрока`);
        });
    },
    [close, data, dispatch, info, teamId, t, toastApiError],
  );

  const modal = useCallback(() => {
    return (
      <Modal show={isOpen} onClose={close} size="md">
        <Formik
          initialValues={
            formValues || {
              surname: '',
              name: '',
              middleName: '',
              birthDate: '',
              photo: '',
            }
          }
          enableReinitialize={false}
          validationSchema={teamPlayerValidator}
          onSubmit={handleSubmit}
        >
          {({ isValid }) => (
            <Form className="flex h-full w-full flex-col">
              <UDModalDirtyGuard />
              <Modal.Body>
                <UDText type="title-3" style={{ fontFamily: 'SF Pro Text', marginBottom: '40px' }}>
                  <Trans>Редактирование</Trans>
                </UDText>
                <div className="flex h-full flex-col gap-4 px-8">
                  <UDFormInput title={t`Фамилия`} name="surname" placeholder={t`Введите фамилию`} errorVisible />
                  <UDFormInput title={t`Имя`} name="name" placeholder={t`Введите имя`} errorVisible />
                  <UDFormInput title={t`Отчество`} name="middleName" placeholder={t`Введите отчество`} errorVisible />
                  <UDFormInput
                    title={t`Дата рождения`}
                    name="birthDate"
                    placeholder={t`Укажите дату рождения`}
                    mask={t({ message: 'дд.мм.гггг', comment: 'Маска поля даты: день, месяц, год' })}
                    formatter={birthDateFormatter}
                    parser={birthDateFormatter}
                    errorVisible
                  />
                  <UDFormDropzone name="photo" preview="inside" crop removable upscale={photoUpscale} />
                </div>
              </Modal.Body>
              <Modal.Footer>
                <UDButton type="submit" variant="primary" className="mt-auto" disabled={!isValid || isLoading} stretch>
                  <Trans>Сохранить</Trans>
                </UDButton>
              </Modal.Footer>
            </Form>
          )}
        </Formik>
      </Modal>
    );
  }, [close, formValues, handleSubmit, isLoading, isOpen, photoUpscale, t]);

  return { isOpen, open: onOpen, Modal: modal };
};

export default useModalEditApplication;
